/* ==========================================================================
   Habits Bottle Shop — "Liquid Glass" Redesign
   Site-wide GSAP + ScrollTrigger behaviors: header glass-scroll state,
   magnetic buttons, cart-count bounce, generic scroll reveals shared across
   sections that don't ship their own inline timeline. Per-section entrance
   timelines (hero kinetic text, video parallax, etc.) live inline in each
   section file so they can target section-scoped ids safely when a page
   has more than one instance.

   Robustness: every block below is guarded by `typeof gsap === 'undefined'`.
   When GSAP fails to load, `html.no-anim` (added by layout/theme.liquid)
   plus the CSS overrides in redesign.css keep all content visible and all
   plain-JS interactivity (search, mobile nav, add-to-cart, video fallback)
   working without this file doing anything.
   ========================================================================== */
document.addEventListener('DOMContentLoaded', function () {

  /* ---------- Cart-count bounce (works with or without GSAP) ---------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    setTimeout(function () {
      var badge = document.querySelector('.rb-icon-btn__count');
      if (badge) {
        badge.classList.remove('rb-bump');
        void badge.offsetWidth;
        badge.classList.add('rb-bump');
      }
    }, 500);
  }, true);

  if (typeof gsap === 'undefined') {
    document.documentElement.classList.add('no-anim');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ---------- Generic reveal helper for anything tagged .reveal ---------- */
  gsap.utils.toArray('.reveal').forEach(function (el) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
    });
  });

  gsap.utils.toArray('.rb-section__head').forEach(function (el) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
    });
  });

  /* ---------- Staggered grid reveals (category tiles, product cards) ---------- */
  gsap.utils.toArray('.rb-categories__grid').forEach(function (grid) {
    var tiles = grid.querySelectorAll('.rb-cat');
    if (!tiles.length) return;
    gsap.to(tiles, {
      opacity: 1, y: 0, duration: 0.95, ease: 'power3.out', stagger: 0.14,
      scrollTrigger: { trigger: grid, start: 'top 82%', toggleActions: 'play none none none' }
    });
  });

  gsap.utils.toArray('.rb-grid').forEach(function (grid) {
    var cards = grid.querySelectorAll('.rb-card');
    if (!cards.length) return;
    gsap.to(cards, {
      opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.09,
      scrollTrigger: { trigger: grid, start: 'top 85%', toggleActions: 'play none none none' }
    });
  });

  gsap.utils.toArray('.rb-value__grid').forEach(function (grid) {
    var items = grid.querySelectorAll('.rb-glass');
    if (!items.length) return;
    gsap.to(items, {
      opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.13,
      scrollTrigger: { trigger: grid, start: 'top 85%', toggleActions: 'play none none none' }
    });
  });

  gsap.utils.toArray('.rb-trust').forEach(function (row) {
    var badges = row.querySelectorAll('.rb-trust__badge');
    if (!badges.length) return;
    gsap.to(badges, {
      opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08,
      scrollTrigger: { trigger: row, start: 'top 90%', toggleActions: 'play none none none' }
    });
  });

  gsap.utils.toArray('.rb-marquee__item').forEach(function (el, i) {
    gsap.to(el, { opacity: 1, duration: 0.6, delay: Math.min(i * 0.02, 0.6) });
  });

  /* ---------- About / feature / video panels ---------- */
  gsap.utils.toArray('.rb-about__media').forEach(function (el) {
    gsap.to(el, {
      opacity: 1, x: 0, duration: 1.05, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 82%', toggleActions: 'play none none none' }
    });
  });
  gsap.utils.toArray('.rb-about__body .reveal').forEach(function (el, i) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: i * 0.08,
      scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
    });
  });
  gsap.utils.toArray('.rb-video__content').forEach(function (el) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 70%', toggleActions: 'play none none none' }
    });
  });
  gsap.utils.toArray('.rb-feature__panel').forEach(function (el) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 82%', toggleActions: 'play none none none' }
    });
  });
  gsap.utils.toArray('.rb-newsletter__panel .reveal').forEach(function (el, i) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: i * 0.08,
      scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none none' }
    });
  });

  /* ---------- Parallax layers ---------- */
  /* Scroll-scrubbed transforms bypass CSS transitions entirely — GSAP writes
     the transform inline on every scroll tick — so the blanket
     prefers-reduced-motion CSS block elsewhere in redesign.css (which only
     neutralizes transition/animation-duration and the specific .reveal-style
     opacity/transform rules) does nothing to stop this parallax movement.
     Gate all of it behind gsap.matchMedia(), the GSAP-recommended pattern for
     conditional effects, so reduced-motion users get genuinely static
     backgrounds instead of ones that keep drifting on scroll. */
  var parallaxMM = gsap.matchMedia();
  parallaxMM.add('(prefers-reduced-motion: no-preference)', function () {
    gsap.utils.toArray('[data-rb-parallax-bg]').forEach(function (bg) {
      gsap.to(bg, {
        yPercent: 16, ease: 'none',
        scrollTrigger: { trigger: bg.closest('section') || bg, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
    gsap.utils.toArray('[data-rb-parallax-img]').forEach(function (img) {
      gsap.to(img, {
        yPercent: -10, ease: 'none',
        scrollTrigger: { trigger: img.closest('.rb-about__media') || img, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
    gsap.utils.toArray('.rb-cat__media img').forEach(function (img) {
      gsap.to(img, {
        yPercent: -8, ease: 'none',
        scrollTrigger: { trigger: img.closest('.rb-cat'), start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  });

  /* ---------- Header: blur/opacity/tint shift on scroll ---------- */
  var header = document.getElementById('rb-header');
  if (header) {
    ScrollTrigger.create({
      start: 'top -60',
      end: 99999,
      toggleClass: { targets: header, className: 'rb-is-scrolled' }
    });
  }

  /* ---------- Magnetic buttons ---------- */
  /* Gated the same way as the product-page tilt effects (see
     sections/redesign-product-main.liquid's `!('ontouchstart' in window)`
     guard on the serve-card tilt): touch devices rarely fire mousemove, but
     when they do (some Android WebViews synthesize one on tap) there's no
     matching mouseleave to reset the transform, so skip entirely on touch. */
  if (!('ontouchstart' in window)) {
    gsap.utils.toArray('.rb-magnetic').forEach(function (btn) {
      var strength = 0.35;
      function move(e) {
        var rect = btn.getBoundingClientRect();
        var relX = e.clientX - (rect.left + rect.width / 2);
        var relY = e.clientY - (rect.top + rect.height / 2);
        gsap.to(btn, { x: relX * strength, y: relY * strength, duration: 0.5, ease: 'power3.out' });
      }
      function reset() {
        gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      }
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', reset);
    });
  }

  /* ---------- Card hover micro-interaction (spring lift) ---------- */
  if (!('ontouchstart' in window)) {
    gsap.utils.toArray('.rb-card').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        gsap.to(card, { y: -6, duration: 0.45, ease: 'back.out(2)' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { y: 0, duration: 0.5, ease: 'power2.out' });
      });
    });
  }

  ScrollTrigger.refresh();
});
